import { useState, useCallback } from 'react';
import { Check, FlaskConical, Droplets, RefreshCw } from 'lucide-react';
import { useExperiment } from '@/context';
import { pHMeter, Beaker } from '@/components/lab';
import LabBench from '../LabBench';
import InfoBox from '../InfoBox';

interface CalibrationPoint {
  buffer: string;
  reading: number | null;
  set: boolean;
  verified?: boolean;
}

interface CalibrationState {
  pH7: CalibrationPoint;
  pH4: CalibrationPoint;
  pH10: CalibrationPoint;
}

// Info content for steps
const stepInfoContent: Record<number, { title: string; content: string; chemicalEquation?: string }> = {
  1: {
    title: 'Electrode Rinsing',
    content: 'Rinsing the electrode with distilled water between measurements prevents contamination and ensures accurate pH readings.'
  },
  2: {
    title: 'pH 7 Calibration',
    content: 'The pH 7 buffer sets the "zero point" for the electrode. At pH 7, the electrode potential is calibrated to a reference value.'
  },
  4: {
    title: 'pH 4 Calibration',
    content: 'The pH 4 buffer establishes the electrode slope (mV/pH). The theoretical Nernstian slope is 59.16 mV/pH at 25°C.',
    chemicalEquation: 'E = E° + (RT/nF)ln[H+]'
  },
  5: {
    title: 'Verification',
    content: 'Checking with a third buffer (pH 10) verifies calibration accuracy. The reading should be within ±0.05 pH units of the expected value.'
  }
};

export default function Part1Calibration() {
  const { state, updatePartData, completeStep, isStepCompleted } = useExperiment();

  // State for info panel
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const partState = state?.experimentData?.calibration || {
    pHMeter: {
      isOn: true,
      isCalibrated: false,
      currentReading: 7.00,
      temperature: 25.0,
      stability: false
    },
    calibration: {
      pH7: { buffer: 'pH 7.00', reading: null, set: false },
      pH4: { buffer: 'pH 4.00', reading: null, set: false },
      pH10: { buffer: 'pH 10.00', reading: null, verified: false }
    } as CalibrationState,
    electrodeRinsed: false,
    electrodeRinsed2: false,
    currentBuffer: null as string | null
  };

  const [stabilizing, setStabilizing] = useState(false);
  const [currentReading, setCurrentReading] = useState(7.00);

  const updateState = useCallback((updates: Record<string, unknown>) => {
    updatePartData('calibration', { ...partState, ...updates });
  }, [partState, updatePartData]);

  const handleRinseElectrode = useCallback(() => {
    setStabilizing(true);
    setTimeout(() => {
      updateState({ electrodeRinsed: true });
      setStabilizing(false);
      completeStep('calibration-1');
    }, 1500);
  }, [updateState, completeStep]);

  const handleCalibratePH7 = useCallback(() => {
    setStabilizing(true);
    // Simulate stabilization with small fluctuations
    let reading = 6.85 + Math.random() * 0.3;
    const interval = setInterval(() => {
      reading = 7.00 + (Math.random() - 0.5) * 0.02;
      setCurrentReading(reading);
    }, 200);

    setTimeout(() => {
      clearInterval(interval);
      setCurrentReading(7.00);
      const newCalibration = {
        ...partState.calibration,
        pH7: { ...partState.calibration.pH7, reading: 7.00, set: true }
      };
      updateState({
        calibration: newCalibration,
        currentBuffer: 'pH7'
      });
      setStabilizing(false);
      completeStep('calibration-2');
    }, 3000);
  }, [partState.calibration, updateState, completeStep]);

  const handleRinseElectrode2 = useCallback(() => {
    setStabilizing(true);
    setTimeout(() => {
      updateState({ electrodeRinsed2: true });
      setStabilizing(false);
      completeStep('calibration-3');
    }, 1500);
  }, [updateState, completeStep]);

  const handleCalibratePH4 = useCallback(() => {
    if (!partState.calibration.pH7.set) return;

    setStabilizing(true);
    let reading = 4.20 + Math.random() * 0.3;
    const interval = setInterval(() => {
      reading = 4.00 + (Math.random() - 0.5) * 0.02;
      setCurrentReading(reading);
    }, 200);

    setTimeout(() => {
      clearInterval(interval);
      setCurrentReading(4.00);
      const newCalibration = {
        ...partState.calibration,
        pH4: { ...partState.calibration.pH4, reading: 4.00, set: true }
      };
      updateState({
        calibration: newCalibration,
        currentBuffer: 'pH4',
        pHMeter: { ...partState.pHMeter, isCalibrated: true }
      });
      setStabilizing(false);
      completeStep('calibration-4');
    }, 3000);
  }, [partState.calibration, partState.pHMeter, updateState, completeStep]);

  const handleVerifyPH10 = useCallback(() => {
    if (!partState.calibration.pH4.set) return;

    setStabilizing(true);
    let reading = 9.80 + Math.random() * 0.4;
    const interval = setInterval(() => {
      reading = 10.00 + (Math.random() - 0.5) * 0.05;
      setCurrentReading(reading);
    }, 200);

    setTimeout(() => {
      clearInterval(interval);
      // pH 10 verification - should read close to 10.00
      const finalReading = 9.98 + Math.random() * 0.04;
      setCurrentReading(finalReading);
      const newCalibration = {
        ...partState.calibration,
        pH10: { ...partState.calibration.pH10, reading: finalReading, verified: true }
      };
      updateState({
        calibration: newCalibration,
        currentBuffer: 'pH10'
      });
      setStabilizing(false);
      completeStep('calibration-5');
    }, 3000);
  }, [partState.calibration, updateState, completeStep]);

  const getBufferColor = (bufferType: string) => {
    switch (bufferType) {
      case 'pH4': return '#FF6B6B'; // Reddish
      case 'pH7': return '#51CF66'; // Green
      case 'pH10': return '#339AF0'; // Blue
      default: return '#FFFFFF';
    }
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part I: pH Meter Calibration</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Rinse Electrode', hint: 'Rinse with distilled water' },
            { id: 2, title: 'Calibrate at pH 7', hint: 'Adjust to 7.00' },
            { id: 3, title: 'Rinse Electrode', hint: 'Rinse before next buffer' },
            { id: 4, title: 'Calibrate at pH 4', hint: 'Adjust slope' },
            { id: 5, title: 'Verify with pH 10', hint: 'Check calibration accuracy' }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${isStepCompleted(`calibration-${step.id}`) ? 'completed' : ''}`}
            >
              <div className={`step-number ${isStepCompleted(`calibration-${step.id}`) ? 'done' : ''}`}>
                {isStepCompleted(`calibration-${step.id}`) ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className="step-title">{step.title}</span>
                  {stepInfoContent[step.id] && (
                    <button
                      className="step-info-btn"
                      onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                    >
                      ?
                    </button>
                  )}
                </div>
                <span className="step-hint">{step.hint}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Calibration Status */}
        <div className="calibration-status">
          <h4>Calibration Points</h4>
          <div className="cal-point">
            <span>pH 7.00 (Zero):</span>
            <span className={partState.calibration.pH7.set ? 'set' : ''}>
              {partState.calibration.pH7.set ? '7.00' : 'Not Set'}
            </span>
          </div>
          <div className="cal-point">
            <span>pH 4.00 (Slope):</span>
            <span className={partState.calibration.pH4.set ? 'set' : ''}>
              {partState.calibration.pH4.set ? '4.00' : 'Not Set'}
            </span>
          </div>
          <div className="cal-point">
            <span>pH 10.00 (Verify):</span>
            <span className={partState.calibration?.pH10?.verified ? 'verified' : ''}>
              {partState.calibration?.pH10?.verified
                ? partState.calibration.pH10.reading?.toFixed(2)
                : 'Not Verified'}
            </span>
          </div>
        </div>
      </div>

      <LabBench
        experimentName="pH Meter Calibration"
        completedCount={state?.completedSteps.filter(s => s.startsWith('calibration-')).length || 0}
        totalSteps={5}
        equipment={['pH meter', 'Buffer solutions (pH 4, 7, 10)', 'Beakers', 'Wash bottle']}
        labMode="guided"
        currentStep={state?.currentStep || 1}
      >
        <div className="calibration-workspace">
          <div className="equipment-row">
            {/* pH Meter */}
            <div className="ph-meter-station">
              <pHMeter
                reading={currentReading}
                inSolution={partState.currentBuffer !== null}
              />
              <span className="equipment-label">pH Meter</span>
              {stabilizing && (
                <div className="stabilizing-indicator">
                  <RefreshCw size={16} className="spin" />
                  Stabilizing...
                </div>
              )}
            </div>

            {/* Buffer Solutions */}
            <div className="buffers-row">
              <div className="buffer-station">
                <Beaker
                  size={100}
                  fillLevel={70}
                  fillColor={getBufferColor('pH7')}
                  label="pH 7.00"
                  active={
                    !isStepCompleted('calibration-2') &&
                    partState.electrodeRinsed
                  }
                  onClick={() => {
                    if (!isStepCompleted('calibration-2') && partState.electrodeRinsed) {
                      handleCalibratePH7();
                    }
                  }}
                />
              </div>
              <div className="buffer-station">
                <Beaker
                  size={100}
                  fillLevel={70}
                  fillColor={getBufferColor('pH4')}
                  label="pH 4.00"
                  active={
                    !isStepCompleted('calibration-4') &&
                    partState.electrodeRinsed2 &&
                    partState.calibration.pH7.set
                  }
                  onClick={() => {
                    if (!isStepCompleted('calibration-4') && partState.electrodeRinsed2) {
                      handleCalibratePH4();
                    }
                  }}
                />
              </div>
              <div className="buffer-station">
                <Beaker
                  size={100}
                  fillLevel={70}
                  fillColor={getBufferColor('pH10')}
                  label="pH 10.00"
                  active={
                    !isStepCompleted('calibration-5') &&
                    partState.calibration.pH4.set
                  }
                  onClick={() => {
                    if (!isStepCompleted('calibration-5') && partState.calibration.pH4.set) {
                      handleVerifyPH10();
                    }
                  }}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            {!isStepCompleted('calibration-1') && (
              <button className="action-btn primary" onClick={handleRinseElectrode}>
                <Droplets size={18} />
                Rinse Electrode with DI Water
              </button>
            )}

            {isStepCompleted('calibration-2') && !isStepCompleted('calibration-3') && (
              <button className="action-btn primary" onClick={handleRinseElectrode2}>
                <Droplets size={18} />
                Rinse Electrode Again
              </button>
            )}

            {partState.pHMeter.isCalibrated && !isStepCompleted('calibration-5') && (
              <div className="info-message">
                <Check size={20} />
                Two-point calibration complete. Verify with pH 10 buffer.
              </div>
            )}

            {isStepCompleted('calibration-5') && (
              <div className="success-message">
                <Check size={24} />
                <div>
                  <strong>Calibration Complete!</strong>
                  <p>pH Meter is ready for titration.</p>
                  <p>Verification at pH 10: {partState.calibration.pH10.reading?.toFixed(2)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Calibration Info */}
          <div className="calibration-info">
            <div className="info-item">
              <span className="info-label">Temperature:</span>
              <span className="info-value">25.0°C</span>
            </div>
            <div className="info-item">
              <span className="info-label">Theoretical Slope:</span>
              <span className="info-value">59.16 mV/pH</span>
            </div>
            <div className="info-item">
              <span className="info-label">Status:</span>
              <span className={`info-value ${partState.pHMeter.isCalibrated ? 'calibrated' : ''}`}>
                {partState.pHMeter.isCalibrated ? 'Calibrated' : 'Uncalibrated'}
              </span>
            </div>
          </div>
        </div>
      </LabBench>

      <style>{`
        .calibration-workspace {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 30px;
          padding: 20px;
          width: 100%;
        }

        .equipment-row {
          display: flex;
          gap: 60px;
          align-items: flex-end;
        }

        .ph-meter-station {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .buffers-row {
          display: flex;
          gap: 20px;
        }

        .buffer-station {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .equipment-label {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .stabilizing-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: rgba(255, 165, 0, 0.2);
          border-radius: 20px;
          font-size: 12px;
          color: #FFA500;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .action-buttons {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .action-btn.primary {
          background: var(--primary);
          color: white;
        }

        .action-btn:hover {
          transform: translateY(-2px);
        }

        .info-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 20px;
          background: var(--bg-elevated);
          border-radius: 8px;
          color: var(--secondary);
          font-size: 14px;
        }

        .success-message {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px 24px;
          background: rgba(0, 168, 107, 0.15);
          border: 2px solid var(--secondary);
          border-radius: 12px;
          color: var(--secondary);
        }

        .success-message strong {
          font-size: 16px;
          display: block;
          margin-bottom: 4px;
        }

        .success-message p {
          margin: 0;
          font-size: 13px;
          opacity: 0.9;
        }

        .calibration-info {
          display: flex;
          gap: 32px;
          padding: 16px 24px;
          background: var(--bg-elevated);
          border-radius: 8px;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .info-label {
          font-size: 11px;
          color: var(--text-muted);
          text-transform: uppercase;
        }

        .info-value {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          font-family: 'Courier New', monospace;
        }

        .info-value.calibrated {
          color: var(--secondary);
        }

        .calibration-status {
          margin-top: 20px;
          padding: 12px;
          background: var(--bg-elevated);
          border-radius: 8px;
        }

        .calibration-status h4 {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0 0 12px 0;
          text-transform: uppercase;
        }

        .cal-point {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          padding: 6px 0;
          border-bottom: 1px solid var(--border);
        }

        .cal-point:last-child {
          border-bottom: none;
        }

        .cal-point span:first-child {
          color: var(--text-secondary);
        }

        .cal-point span:last-child {
          color: var(--text-muted);
        }

        .cal-point span.set, .cal-point span.verified {
          color: var(--secondary);
          font-weight: 600;
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }

        .step-info-btn:hover {
          transform: scale(1.1);
        }
      `}</style>

      {/* Info Box */}
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp11"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
